'use client';

import { useMemo } from 'react';
import {
  Gender,
  MatchType,
  TournamentType,
  type KnockoutNode,
} from '@/types/tournament.model';
import { Sport } from '@/types';

// Helper function to flatten the tree structure
function flattenKnockoutTree(
  node: KnockoutNode | null | undefined,
  result: Record<string, KnockoutNode> = {},
  nodeId?: string,
): Record<string, KnockoutNode> {
  if (!node) return result;

  // Use the node's ID if available, otherwise generate one
  const id = nodeId || `node_${Object.keys(result).length}`;

  // Add the current node to the result
  result[id] = node;

  // Recursively process left and right children
  if (node.leftChild) {
    flattenKnockoutTree(node.leftChild, result, `${id}_left`);
  }

  if (node.rightChild) {
    flattenKnockoutTree(node.rightChild, result, `${id}_right`);
  }

  return result;
}

/** Display name of each round of a bracket whose final is round [maxRound]. */
export function knockoutRoundNames(maxRound: number): Record<number, string> {
  const roundNames: Record<number, string> = {};

  if (maxRound > 4) {
    // For tournaments with more than 4 rounds, use generic names
    for (let i = 1; i <= maxRound; i++) {
      roundNames[i] = `Ronda ${i}`;
    }
    roundNames[maxRound] = 'Final';
  } else {
    // Use specific names for common tournament structures
    const commonNames: Record<number, string> = {
      1: 'Octavos de Final',
      2: 'Cuartos de Final',
      3: 'Semifinales',
      4: 'Final',
    };

    // Assign names based on the number of rounds
    for (let i = 1; i <= maxRound; i++) {
      const offset = 4 - maxRound;
      const nameIndex = i + offset;
      roundNames[i] =
        nameIndex > 0 && nameIndex <= 4 ? commonNames[nameIndex] : `Ronda ${i}`;
    }

    // Ensure the last round is always "Final"
    roundNames[maxRound] = 'Final';
  }

  return roundNames;
}

export function useKnockoutStageData(
  allNodes: Record<string, KnockoutNode | undefined>,
  rootNode?: KnockoutNode | null,
) {
  return useMemo(() => {
    // If allNodes is empty but we have a rootNode, flatten the tree
    const nodes =
      Object.keys(allNodes).length === 0 && rootNode
        ? flattenKnockoutTree(rootNode)
        : allNodes;

    // Initialize an object to store nodes by round
    const rounds: Record<number, KnockoutNode[]> = {};
    let maxRound = 0;

    // Process all nodes and group them by round
    Object.values(nodes).forEach((node) => {
      if (node && node.round) {
        if (!rounds[node.round]) {
          rounds[node.round] = [];
        }
        rounds[node.round].push(node);
        maxRound = Math.max(maxRound, node.round);
      }
    });

    // If we have a rootNode but no maxRound was found, determine it from the tree depth
    if (maxRound === 0 && rootNode) {
      // Determine the maximum round from the rootNode
      if (rootNode.round) {
        maxRound = rootNode.round;
      }
      // else if (rootNode.hasLeftChild && rootNode.hasRightChild) {
      //   // If rootNode has both children, it's likely the final round
      //   maxRound = rootNode.rootNodeRound || 4; // Default to 4 if rootNodeRound is not available
      // }
    }

    // If there's no maxRound, default to at least showing a final
    if (maxRound === 0) {
      maxRound = 4;
    }

    const roundNames = knockoutRoundNames(maxRound);

    // Generate empty matches for rounds that don't have matches yet
    // This ensures the bracket structure is complete
    for (let i = 1; i <= maxRound; i++) {
      if (!rounds[i]) {
        rounds[i] = [];
      }

      // Calculate expected number of matches for this round
      // In a knockout tournament, round 1 has 2^(maxRound-1) matches,
      // round 2 has 2^(maxRound-2) matches, and so on
      const expectedMatches = Math.pow(2, maxRound - i);

      // If we don't have enough matches, add empty placeholders
      while (rounds[i].length < expectedMatches) {
        rounds[i].push({
          round: i,
          match: {
            id: `placeholder_${i}_${rounds[i].length}`,
            team1: undefined,
            team2: undefined,
            startDate: new Date().toISOString(),
            endDate: new Date().toISOString(),
            playersPerTeam: 2,
            sport: {
              name: '',
              uid: 'PADEL',
            } as Sport,
            matchType: MatchType.TOURNAMENT,
            gender: Gender.ANY,
            competitiveMatchType: TournamentType.STRICT,
          },
        });
      }
    }

    return {
      roundsByLevel: rounds as Record<number, KnockoutNode[]>,
      maxRound,
      roundNames,
    };
  }, [allNodes, rootNode]);
}
